import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Plus, Search, RefreshCw, FileText, Blocks, Grid3x3 as Grid3X3, Table2, ExternalLink, CreditCard as Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import TranscriptModal from './TranscriptModal';
import { useAuth } from '../../context/AuthContext';
import { useAppContext } from '../../context/AppContext';
import { Transcript } from '../../types';

function chunksIndexLabel(status: string | undefined): string {
  switch (status ?? 'none') {
    case 'ready':
      return 'Search index ready';
    case 'pending':
      return 'Indexing…';
    case 'error':
      return 'Index error';
    default:
      return 'Not indexed';
  }
}

/** Escape `%` / `_` / `"` so user search text is literal in ILIKE / PostgREST or-filters. */
function escapeIlike(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_').replace(/"/g, '');
}

type ViewMode = 'cards' | 'table';
type TaskFilter = 'all' | 'no_tasks' | 'incomplete';

const TASK_FILTER_OPTIONS: { id: TaskFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'no_tasks', label: 'No tasks' },
  { id: 'incomplete', label: 'Incomplete tasks' },
];

const OPEN_TASK_STATUSES = ['pending', 'in_progress'] as const;
const ITEMS_PER_PAGE = 20;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const SEARCH_DEBOUNCE_MS = 300;

type FilterCounts = { all: number; no_tasks: number; incomplete: number };

type IdScope =
  | { mode: 'none' }
  | { mode: 'include'; ids: string[] }
  | { mode: 'exclude'; ids: string[] }
  | { mode: 'empty' };

function uniqueTranscriptIds(rows: { transcript_id: string | null }[] | null): string[] {
  return [
    ...new Set(
      (rows ?? [])
        .map((row) => row.transcript_id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0),
    ),
  ];
}

function applyTitleSearch<T extends { or: (filters: string) => T }>(query: T, pattern: string | null): T {
  if (!pattern) return query;
  return query.or(`title.ilike."${pattern}",summary.ilike."${pattern}"`);
}

function applyIdScope<T extends { in: (col: string, vals: string[]) => T; not: (col: string, op: string, val: string) => T }>(
  query: T,
  idScope: IdScope,
): T {
  if (idScope.mode === 'include') return query.in('id', idScope.ids);
  if (idScope.mode === 'exclude') return query.not('id', 'in', `(${idScope.ids.join(',')})`);
  return query;
}

async function resolveTaskFilterIds(filter: TaskFilter): Promise<IdScope> {
  if (filter === 'all') return { mode: 'none' };

  if (filter === 'incomplete') {
    const { data, error } = await supabase
      .from('tasks')
      .select('transcript_id')
      .in('status', [...OPEN_TASK_STATUSES])
      .not('transcript_id', 'is', null);

    if (error) throw error;

    const ids = uniqueTranscriptIds(data);
    return ids.length === 0 ? { mode: 'empty' } : { mode: 'include', ids };
  }

  // no_tasks — literally zero rows in `tasks` linked to this transcript
  const { data, error } = await supabase
    .from('tasks')
    .select('transcript_id')
    .not('transcript_id', 'is', null);

  if (error) throw error;

  const ids = uniqueTranscriptIds(data);
  return ids.length === 0 ? { mode: 'none' } : { mode: 'exclude', ids };
}

async function countTranscriptsForScope(pattern: string | null, idScope: IdScope): Promise<number> {
  if (idScope.mode === 'empty') return 0;

  let query = supabase.from('transcripts').select('*', { count: 'exact', head: true });
  query = applyTitleSearch(query, pattern);
  query = applyIdScope(query, idScope);

  const { count, error } = await query;
  if (error) throw error;
  return count || 0;
}

async function loadFilterCounts(searchTerm: string): Promise<FilterCounts> {
  const q = searchTerm.trim();
  const pattern = q ? `%${escapeIlike(q)}%` : null;

  const [all, noTasksScope, incompleteScope] = await Promise.all([
    countTranscriptsForScope(pattern, { mode: 'none' }),
    resolveTaskFilterIds('no_tasks'),
    resolveTaskFilterIds('incomplete'),
  ]);

  const [no_tasks, incomplete] = await Promise.all([
    countTranscriptsForScope(pattern, noTasksScope),
    countTranscriptsForScope(pattern, incompleteScope),
  ]);

  return { all, no_tasks, incomplete };
}

const TranscriptsView: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { transcriptsCache, setTranscriptsCache } = useAppContext();
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [taskFilter, setTaskFilter] = useState<TaskFilter>('all');
  const [filterCounts, setFilterCounts] = useState<FilterCounts | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState<Transcript | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const transcriptsRef = useRef<Transcript[]>([]);
  const offsetRef = useRef(0);
  const taskFilterRef = useRef<TaskFilter>(taskFilter);

  useEffect(() => {
    transcriptsRef.current = transcripts;
  }, [transcripts]);

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  useEffect(() => {
    taskFilterRef.current = taskFilter;
  }, [taskFilter]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchQuery.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const fetchTranscripts = useCallback(async (
    isLoadMore = false,
    searchTerm = debouncedSearch,
    filter: TaskFilter = taskFilterRef.current,
  ) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setOffset(0);
        offsetRef.current = 0;
      }

      const currentOffset = isLoadMore ? offsetRef.current : 0;
      const q = searchTerm.trim();
      const pattern = q ? `%${escapeIlike(q)}%` : null;
      const useCache = !q && filter === 'all';

      const idScope = await resolveTaskFilterIds(filter);
      if (idScope.mode === 'empty') {
        setTranscripts([]);
        setHasMore(false);
        setOffset(0);
        offsetRef.current = 0;
        setTranscriptsCache(null);
        return;
      }

      let listQuery = supabase
        .from('transcripts')
        .select('*')
        .order('meeting_date', { ascending: false })
        .range(currentOffset, currentOffset + ITEMS_PER_PAGE - 1);

      let countQuery = supabase.from('transcripts').select('*', { count: 'exact', head: true });

      listQuery = applyTitleSearch(listQuery, pattern);
      countQuery = applyTitleSearch(countQuery, pattern);
      listQuery = applyIdScope(listQuery, idScope);
      countQuery = applyIdScope(countQuery, idScope);

      const { data: transcriptsData, error: transcriptsError } = await listQuery;

      if (transcriptsError) {
        throw transcriptsError;
      }

      const { count } = await countQuery;

      setHasMore((count || 0) > currentOffset + ITEMS_PER_PAGE);

      const transcriptsWithCounts = await Promise.all(
        (transcriptsData || []).map(async (transcript) => {
          const { count: blockCount } = await supabase
            .from('content_blocks')
            .select('*', { count: 'exact', head: true })
            .eq('transcript_id', transcript.id);

          return {
            ...transcript,
            content_block_count: blockCount || 0
          };
        })
      );

      if (isLoadMore) {
        const newTranscripts = [...transcriptsRef.current, ...transcriptsWithCounts];
        setTranscripts(newTranscripts);
        const newOffset = currentOffset + ITEMS_PER_PAGE;
        setOffset(newOffset);
        offsetRef.current = newOffset;
        if (useCache) {
          setTranscriptsCache({
            transcripts: newTranscripts,
            offset: newOffset,
            hasMore: (count || 0) > newOffset,
            lastFetched: Date.now()
          });
        }
      } else {
        setTranscripts(transcriptsWithCounts);
        setOffset(ITEMS_PER_PAGE);
        offsetRef.current = ITEMS_PER_PAGE;
        if (useCache) {
          setTranscriptsCache({
            transcripts: transcriptsWithCounts,
            offset: ITEMS_PER_PAGE,
            hasMore: (count || 0) > ITEMS_PER_PAGE,
            lastFetched: Date.now()
          });
        } else {
          setTranscriptsCache(null);
        }
      }
    } catch (error: any) {
      toast.error(`Error fetching transcripts: ${error.message}`);
      console.error('Transcripts fetch error:', error);
      if (!isLoadMore) {
        setTranscripts([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [debouncedSearch, setTranscriptsCache]);

  const handleLoadMore = () => {
    void fetchTranscripts(true, debouncedSearch, taskFilter);
  };

  useEffect(() => {
    if (!user) return;

    // Fresh list browse can use cache; search / task filters always hit the DB.
    if (
      !debouncedSearch &&
      taskFilter === 'all' &&
      transcriptsCache &&
      Date.now() - transcriptsCache.lastFetched < CACHE_DURATION
    ) {
      setTranscripts(transcriptsCache.transcripts);
      setOffset(transcriptsCache.offset);
      setHasMore(transcriptsCache.hasMore);
      setLoading(false);
    } else {
      void fetchTranscripts(false, debouncedSearch, taskFilter);
    }

    void loadFilterCounts(debouncedSearch)
      .then(setFilterCounts)
      .catch((e: unknown) => {
        console.error('Filter counts error:', e);
      });
  }, [user, debouncedSearch, taskFilter, fetchTranscripts]);

  const handleAddTranscript = () => {
    setCurrentTranscript(null);
    setIsModalOpen(true);
  };

  const openTranscriptDetail = (transcript: Transcript) => {
    navigate(`/transcripts/${transcript.id}`);
  };

  const openTranscriptEdit = (transcript: Transcript) => {
    navigate(`/transcripts/${transcript.id}?tab=edit`);
  };

  const handleDeleteTranscript = async (e: React.MouseEvent, transcriptId: string) => {
    e.stopPropagation();

    const confirmDelete = window.confirm('Are you sure you want to delete this transcript? This action cannot be undone.');
    if (!confirmDelete) return;

    try {
      const { error } = await supabase
        .from('transcripts')
        .delete()
        .eq('id', transcriptId);

      if (error) throw error;

      toast.success('Transcript deleted successfully');
      setTranscriptsCache(null);
      void fetchTranscripts(false, debouncedSearch, taskFilter);
      void loadFilterCounts(debouncedSearch).then(setFilterCounts).catch(() => undefined);
    } catch (error: any) {
      toast.error(`Error deleting transcript: ${error.message}`);
    }
  };

  const handleModalSuccess = () => {
    setTranscriptsCache(null);
    void fetchTranscripts(false, debouncedSearch, taskFilter);
    void loadFilterCounts(debouncedSearch).then(setFilterCounts).catch(() => undefined);
  };

  const handleOpenExtractor = (e: React.MouseEvent, transcriptId: string) => {
    e.stopPropagation();
    window.open(`https://lccto-content-extractor.netlify.app/?transcript_id=${transcriptId}`, '_blank');
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <FileText className="w-8 h-8 mr-3 text-emerald-500" />
            Meeting Transcripts
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage and organize your meeting transcripts
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setViewMode('cards')}
              className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'cards'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Grid3X3 className="w-4 h-4 mr-2" />
              Cards
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'table'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Table2 className="w-4 h-4 mr-2" />
              Table
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by title or name…"
              className="pl-10 pr-4 py-2.5 w-64 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-shadow"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            onClick={handleAddTranscript}
            className="flex items-center justify-center p-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
            title="New Transcript"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mr-1">
          Tasks
        </span>
        <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          {TASK_FILTER_OPTIONS.map((option) => {
            const count = filterCounts?.[option.id];
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => setTaskFilter(option.id)}
                title={
                  option.id === 'no_tasks'
                    ? 'Transcripts with zero linked task rows'
                    : option.id === 'incomplete'
                      ? 'Transcripts with at least one pending or in-progress task'
                      : 'All transcripts'
                }
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  taskFilter === option.id
                    ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {option.label}
                {typeof count === 'number' ? (
                  <span className="ml-1.5 text-xs tabular-nums text-gray-500 dark:text-gray-400">
                    ({count})
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {viewMode === 'cards' ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              <div className="col-span-full flex justify-center items-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-emerald-500" />
              </div>
            ) : transcripts.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
                  No transcripts found
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  {searchQuery || taskFilter !== 'all'
                    ? 'Try adjusting your search or task filter'
                    : 'Start by adding a transcript'}
                </p>
              </div>
            ) : (
              transcripts.map((transcript) => (
              <div
                key={transcript.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => openTranscriptDetail(transcript)}
              >
                <div className="flex items-start justify-between mb-2 gap-2">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    {transcript.title}
                  </h3>
                  <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0">
                    <span
                      className={`text-xs px-2 py-1 rounded-full font-medium ${
                        transcript.transcript_chunks_status === 'ready'
                          ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-200'
                          : transcript.transcript_chunks_status === 'pending'
                            ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-100'
                            : transcript.transcript_chunks_status === 'error'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                      title="Vector Q&A index"
                    >
                      {chunksIndexLabel(transcript.transcript_chunks_status)}
                    </span>
                    {transcript.content_block_count > 0 && (
                      <div className="flex items-center text-sm text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1 rounded-full">
                        <Blocks className="w-4 h-4 mr-1" />
                        {transcript.content_block_count}
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-sm line-clamp-3 mb-4">
                  {transcript.summary || transcript.content}
                </p>
                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
                  <span className="mr-2">Meeting Date:</span>
                  {formatDate(transcript.meeting_date)}
                </div>
                {transcript.participants.length > 0 && (
                  <div className="mb-3">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Participants
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {transcript.participants.map((participant, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300"
                        >
                          {participant}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {transcript.tags.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tags
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {transcript.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
            )}
          </div>

          {!loading && hasMore && (
            <div className="flex justify-center mt-8">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingMore ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Load More'
                )}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Blocks
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Index
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Participants
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center">
                      <RefreshCw className="w-6 h-6 animate-spin text-emerald-500 mx-auto" />
                    </td>
                  </tr>
                ) : transcripts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                      No transcripts found
                    </td>
                  </tr>
                ) : (
                  transcripts.map((transcript) => (
                    <tr
                      key={transcript.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      onClick={() => openTranscriptDetail(transcript)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {transcript.title}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                              {transcript.summary || transcript.content.substring(0, 100) + '...'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {formatDate(transcript.meeting_date)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {transcript.content_block_count > 0 ? (
                            <div className="flex items-center text-sm text-emerald-600 dark:text-emerald-400">
                              <Blocks className="w-4 h-4 mr-1" />
                              {transcript.content_block_count}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">0</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`text-xs font-medium ${
                            transcript.transcript_chunks_status === 'ready'
                              ? 'text-violet-700 dark:text-violet-300'
                              : transcript.transcript_chunks_status === 'error'
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-gray-500 dark:text-gray-400'
                          }`}
                        >
                          {chunksIndexLabel(transcript.transcript_chunks_status)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {transcript.participants.slice(0, 2).map((participant, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300"
                            >
                              {participant}
                            </span>
                          ))}
                          {transcript.participants.length > 2 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              +{transcript.participants.length - 2} more
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => handleOpenExtractor(e, transcript.id)}
                            className="p-1 text-gray-500 hover:text-emerald-500 transition-colors"
                            title="Extract Content"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openTranscriptEdit(transcript);
                            }}
                            className="p-1 text-gray-500 hover:text-blue-500 transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => handleDeleteTranscript(e, transcript.id)}
                            className="p-1 text-gray-500 hover:text-red-500 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!loading && hasMore && (
            <div className="flex justify-center p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingMore ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Load More'
                )}
              </button>
            </div>
          )}
        </div>
      )}

      <TranscriptModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        transcript={currentTranscript}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default TranscriptsView;